import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';

type TBannerTimerProps = {
    displayUnitsBottom?: boolean;
    isSlider?: boolean;
    hideDays?: boolean;
};

type TProps = {
    endTime: string;
    disableDays?: boolean;
    hideDays?: boolean;
};
function useTimer(
    endTime: string,
    disableDays = false,
): [number, number, number, number, boolean, boolean, boolean, boolean] {
    const [days, setDays] = useState<number>(0);
    const [hours, setHours] = useState<number>(0);
    const [minutes, setMinutes] = useState<number>(0);
    const [seconds, setSeconds] = useState<number>(0);

    const [secondsStart, setSecondsStart] = useState<boolean>(false);
    const [minutesStart, setMinutesStart] = useState<boolean>(false);
    const [hoursStart, setHoursStart] = useState<boolean>(false);
    const [daysStart, setDaysStart] = useState<boolean>(false);

    useEffect(() => {
        setMinutesStart(true);
    }, [minutes]);

    useEffect(() => {
        setHoursStart(true);
    }, [hours]);

    useEffect(() => {
        setDaysStart(true);
    }, [days]);

    useEffect(() => {
        const timer = (interval: number): void => {
            const countDownDate = new Date(endTime).getTime();
            const now = new Date().getTime();
            const distance = countDownDate - now;
            const newDays = Math.floor(distance / (1000 * 60 * 60 * 24));
            let newHours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            setSecondsStart(false);
            setMinutesStart(false);
            setHoursStart(false);
            setDaysStart(false);

            // If the days are disabled, add them to hours
            if (disableDays) {
                newHours = newDays * 24 + newHours;
            }
            const newMinutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

            const newSeconds = Math.floor((distance % (1000 * 60)) / 1000);

            if (distance < 0) {
                if (interval) {
                    clearInterval(interval);
                }
                setDays(0);
                setHours(0);
                setMinutes(0);
                setSeconds(0);
            } else {
                setDays(newDays);
                setHours(newHours);
                setMinutes(newMinutes);
                setSeconds(newSeconds);
                setSecondsStart(true);
            }
        };
        const interval = setInterval(timer, 1000);

        return (): void => clearInterval(interval);
    }, [disableDays, endTime]);

    return [days, hours, minutes, seconds, secondsStart, minutesStart, hoursStart, daysStart];
}

const zerofill = (value = 0): string => {
    return value < 10 && value > -1 ? `0${value}` : `${value}`;
};

const flipTop = keyframes`
    0% {
        transform: translate(-50%, -50%) rotateX(0deg);
        box-shadow: inset 0px 0px 10px 1px rgba(0, 0, 0, 0.1);
    }

    0%,
    99% {
        opacity: 1;
    }

    100% {
        box-shadow: inset 0px 0px 1px 0px rgba(0, 0, 0, 0.4);
        transform: translate(-50%, -50%) rotateX(-90deg);
        opacity: 0;
    }
`;
const flipBottom = keyframes`
    0%,
    50% {
        transform: rotateX(90deg);
        box-shadow: inset 0px 0px 1px 0px rgba(0, 0, 0, 0.4);
        box-shadow: inset 0px 0px 20px 1px rgba(0, 0, 0, 0.2);
    }

    100% {
        transform: rotateX(0deg);
        box-shadow: inset 0px 0px 0px 0px rgba(0, 0, 0, 0) , 0px 2px 10px -3px rgba(0,0,0,.2);
    }
`;

const FlipCardTimer = ({ disableDays, endTime, hideDays }: TProps): JSX.Element => {
    const [days, hours, minutes, seconds, secondsStart, minutesStart, hoursStart, daysStart] = useTimer(
        endTime,
        disableDays,
    );

    const { t } = useTranslation('common');
    const displayUnit = false;
    const seperator = true;
    return (
        <BannerTimerWrapper>
            {!!days && days > 0 && !disableDays && !hideDays && (
                <>
                    <div className="card">
                        <div className="flipClockContainer">
                            <CSSTransition in={daysStart} timeout={1000} classNames="turn">
                                <div className="flipCardCover">
                                    <div className="flipCard">
                                        <div className="flipCardTop">
                                            <span className="flipCardTopText">{zerofill(days)}</span>
                                        </div>
                                        <div className="flipCardBottom" data-value={zerofill(days)} />
                                        <div className="flipCardBack" data-value={zerofill(days + 1)} />
                                        <div className="flipCardBackBottom" data-value={zerofill(days + 1)} />
                                    </div>
                                </div>
                            </CSSTransition>
                        </div>
                        {displayUnit && (
                            <div className="timeunit">{days === 1 ? t('_timer-day') : t('_timer-days')}</div>
                        )}
                    </div>
                    {seperator && <span className="seperator">:</span>}
                </>
            )}

            {(!!hours || hours === 0) && (
                <>
                    <div className="card">
                        <div className="flipClockContainer">
                            <CSSTransition in={hoursStart} timeout={1000} classNames="turn">
                                <div className="flipCardCover">
                                    <div className="flipCard">
                                        <div className="flipCardTop">
                                            <span className="flipCardTopText">{zerofill(hours)}</span>
                                        </div>
                                        <div className="flipCardBottom" data-value={zerofill(hours)} />
                                        <div
                                            className="flipCardBack"
                                            data-value={zerofill(hours + 1 !== 24 ? hours + 1 : 0)}
                                        />
                                        <div
                                            className="flipCardBackBottom"
                                            data-value={zerofill(hours + 1 !== 24 ? hours + 1 : 0)}
                                        />
                                    </div>
                                </div>
                            </CSSTransition>
                        </div>
                        {displayUnit && (
                            <div className="timeunit">{hours === 1 ? t('_timer-hours') : t('_timer-hours')}</div>
                        )}
                    </div>
                    {seperator && <span className="seperator">:</span>}
                </>
            )}

            {(!!minutes || minutes === 0) && (
                <>
                    <div className="card">
                        <div className="flipClockContainer">
                            <CSSTransition in={minutesStart} timeout={1000} classNames="turn">
                                <div className="flipCardCover">
                                    <div className="flipCard">
                                        <div className="flipCardTop">
                                            <span className="flipCardTopText">{zerofill(minutes)}</span>
                                        </div>
                                        <div className="flipCardBottom" data-value={zerofill(minutes)} />
                                        <div
                                            className="flipCardBack"
                                            data-value={zerofill(minutes + 1 !== 60 ? minutes + 1 : 0)}
                                        />
                                        <div
                                            className="flipCardBackBottom"
                                            data-value={zerofill(minutes + 1 !== 60 ? minutes + 1 : 0)}
                                        />
                                    </div>
                                </div>
                            </CSSTransition>
                        </div>
                        {displayUnit && (
                            <div className="timeunit">{minutes === 1 ? t('_timer-minutes') : t('_timer-minutes')}</div>
                        )}
                    </div>
                    {seperator && <span className="seperator">:</span>}
                </>
            )}

            {(!!seconds || seconds === 0) && (
                <>
                    <div className="card">
                        <div className="flipClockContainer">
                            <CSSTransition in={secondsStart} timeout={1000} classNames="turn">
                                <div className="flipCardCover">
                                    <div className="flipCard">
                                        <div className="flipCardTop">
                                            <span className="flipCardTopText">{zerofill(seconds)}</span>
                                        </div>
                                        <div className="flipCardBottom" data-value={zerofill(seconds)} />
                                        <div
                                            className="flipCardBack"
                                            data-value={zerofill(seconds + 1 !== 60 ? seconds + 1 : 0)}
                                        />
                                        <div
                                            className="flipCardBackBottom"
                                            data-value={zerofill(seconds + 1 !== 60 ? seconds + 1 : 0)}
                                        />
                                    </div>
                                </div>
                            </CSSTransition>
                        </div>
                        {displayUnit && (
                            <div className="timeunit">{seconds === 1 ? t('_timer-seconds') : t('_timer-seconds')}</div>
                        )}
                    </div>
                </>
            )}
        </BannerTimerWrapper>
    );
};

export default FlipCardTimer;

const commonSpace = 12;

// following are for light theme ----------->
const topColor = '#978d01';
const bottomColor = '#8a8100';
const topBackgroundColor = '#f7f7f7';
const bottomBackgroundColor = '#fff';
const middleBorder = 'none';

// following are for dark theme ----------->
// const topColor = '#ccc';
// const bottomColor = '#fff';
// const topBackgroundColor = '#222';
// const bottomBackgroundColor = '#393939';
// const middleBorder = '1px solid #000';

const borderRadius = 5;

const BannerTimerWrapper = styled.div<TBannerTimerProps>`
    display: flex;
    ${({ theme: { isRTL } }) => (isRTL ? 'flex-direction: row-reverse;' : '')}
    margin-top: 7px;

    .card {
        display: flex;
        justify-content: space-between;
        flex-direction: column;
    }
    .seperator {
        margin-top: ${commonSpace / 6}px;
        display: inline-flex;
        align-self: stretch;
        height: auto;
        color: #fff;
        font-size: 1rem;
        font-weight: 900;
    }
    .timeunit {
        color: #000;
        text-align: center;
    }
    .flipClockContainer {
        text-align: center;
        perspective: 100px;
        margin: 0 ${commonSpace / 6}px;
        font-size: 16px;
        line-height: 1em;
    }
    *,
    *:before,
    *:after {
        box-sizing: border-box;
    }

    .flipCardCover {
        display: inline-block;
        box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.2);
        pointer-events: none;
        border-radius: ${borderRadius}px;
    }

    .flipCard {
        position: relative;
        font-weight: 900;
    }

    .flipCardTop {
        position: absolute;
        top: 0;
        left: 0;
        height: 0.75em;
        width: 30px;
        overflow: hidden;
        box-shadow: inset 0px 0px 1px 0px rgba(0, 0, 0, 0.4);
        background: ${topBackgroundColor};
        color: ${topColor};
        border-radius: ${borderRadius}px ${borderRadius}px 0 0;
        .flipCardTopText {
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translate(-50%, 50%);
        }
    }

    .flipCardBottom {
        position: absolute;
        left: 0;
        bottom: 0;
        height: 0.75em;
        width: 30px;
        overflow: hidden;
        backface-visibility: hidden;
        transform-style: preserve-3d;
        transform-origin: center top;
        border-top: ${middleBorder};
        z-index: 5;
        opacity: 1;
        transform: rotateX(0deg);
        background: ${bottomBackgroundColor};
        color: ${bottomColor};
        border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
        &::after {
            content: attr(data-value);
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    }

    .flipCardBack {
        height: 1.5em;
        width: 30px;
        overflow: hidden;
        position: relative;
        border-radius: ${borderRadius}px;
        &::before {
            content: attr(data-value);
            position: absolute;
            display: flex;
            justify-content: center;
            align-items: center;
            top: 50%;
            left: 50%;
            width: 100%;
            height: 100%;
            transform: translate(-50%, -50%) rotateX(0deg);
            opacity: 0;
            backface-visibility: hidden;
            transform-style: preserve-3d;
            transform-origin: center bottom;
            background: ${topBackgroundColor};
            color: ${topColor};
            border-radius: ${borderRadius}px;
            box-shadow: inset 0px 0px 1px 0px rgba(0, 0, 0, 0.4);
        }
    }

    .flipCardBackBottom {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 0.75em;
        width: 30px;
        overflow: hidden;
        background: ${bottomBackgroundColor};
        color: ${bottomColor};
        border-top: ${middleBorder};
        border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
        &::after {
            content: attr(data-value);
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    }

    .turn-enter .flipCardBack::before {
        animation: ${flipTop} 0.3s cubic-bezier(0.37, 0.01, 0.94, 0.35);
        animation-fill-mode: both;
    }
    .turn-enter .flipCardBottom {
        animation-fill-mode: both;
        animation: ${flipBottom} 0.6s cubic-bezier(0.15, 0.45, 0.28, 1);
    }
`;
